// Minimal 2D vector helpers (Nature of Code Ch. 1). We use plain {x, y} objects
// and mostly mutate a small set of scratch vectors per tick to avoid GC churn
// when thousands of agents are simulated.

export interface Vec2 {
  x: number;
  y: number;
}

export function vec(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function set(out: Vec2, x: number, y: number): Vec2 {
  out.x = x;
  out.y = y;
  return out;
}

export function copy(out: Vec2, a: Vec2): Vec2 {
  out.x = a.x;
  out.y = a.y;
  return out;
}

export function add(out: Vec2, a: Vec2, b: Vec2): Vec2 {
  out.x = a.x + b.x;
  out.y = a.y + b.y;
  return out;
}

export function sub(out: Vec2, a: Vec2, b: Vec2): Vec2 {
  out.x = a.x - b.x;
  out.y = a.y - b.y;
  return out;
}

export function scale(out: Vec2, a: Vec2, s: number): Vec2 {
  out.x = a.x * s;
  out.y = a.y * s;
  return out;
}

export function addScaled(out: Vec2, a: Vec2, b: Vec2, s: number): Vec2 {
  out.x = a.x + b.x * s;
  out.y = a.y + b.y * s;
  return out;
}

export function mag(a: Vec2): number {
  return Math.hypot(a.x, a.y);
}

export function magSq(a: Vec2): number {
  return a.x * a.x + a.y * a.y;
}

/** Scale `a` to length `len` (no-op on a zero vector). */
export function setMag(out: Vec2, a: Vec2, len: number): Vec2 {
  const m = Math.hypot(a.x, a.y);
  if (m === 0) {
    out.x = 0;
    out.y = 0;
  } else {
    const k = len / m;
    out.x = a.x * k;
    out.y = a.y * k;
  }
  return out;
}

/** Clamp the length of `a` to at most `max`. */
export function limit(out: Vec2, a: Vec2, max: number): Vec2 {
  const mSq = a.x * a.x + a.y * a.y;
  if (mSq > max * max) {
    const k = max / Math.sqrt(mSq);
    out.x = a.x * k;
    out.y = a.y * k;
  } else {
    out.x = a.x;
    out.y = a.y;
  }
  return out;
}

export function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function distSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function fromAngle(out: Vec2, angle: number, len = 1): Vec2 {
  out.x = Math.cos(angle) * len;
  out.y = Math.sin(angle) * len;
  return out;
}
